import { useReducer, useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useTriviaGame } from "./useTriviaGame";
import { useTriviaDaily } from "./useTriviaDaily";
import {
  QUICK_PLAY_CONFIG,
  DAILY_PLAY_CONFIG,
  withSport,
} from "../utils/triviaArcade";
import {
  reduceArcade,
  initialArcadeState,
} from "../utils/triviaArcadeMachine";

export const useTriviaArcade = ({ userStats, sport = "mlb" } = {}) => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [state, dispatch] = useReducer(reduceArcade, initialArcadeState);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timeLimit, setTimeLimit] = useState(30);
  const [showQuitDialog, setShowQuitDialog] = useState(false);
  const [error, setError] = useState(null);
  const questionStartTime = useRef(Date.now());
  const pendingFeedbackResult = useRef(null);
  const startLock = useRef(false);

  const {
    startNewGame,
    bootstrapSession,
    answerQuestion,
    finishGame,
    quitGame,
    resetGame,
    sessionData,
    isStarting: isStartRequestPending,
    isSubmitting,
  } = useTriviaGame();

  const { startDaily } = useTriviaDaily(sport);

  const beginSession = useCallback(
    async (config, gameMode) => {
      if (startLock.current) {
        return { success: false, ignored: true };
      }
      startLock.current = true;
      setError(null);
      dispatch({
        type: "START_PENDING",
        gameMode,
        questionCount: config?.questionCount,
      });
      const startLevel = userStats?.level || 1;
      const startXp = userStats?.totalExperiencePoints || 0;

      try {
        let result;
        if (gameMode === "daily") {
          const dailyResult = await startDaily();
          result = bootstrapSession(dailyResult);
        } else {
          result = await startNewGame(config);
        }

        if (!result?.success) {
          const message = result?.error || "Failed to start game";
          dispatch({ type: "START_FAILED" });
          setError(message);
          return { success: false, error: message };
        }

        const limit = result.questions?.[0]?.timeLimit || config?.timeLimit || 30;
        setTimeLimit(limit);
        setTimeLeft(limit);
        questionStartTime.current = Date.now();
        dispatch({
          type: "START_SUCCESS",
          gameMode,
          questionCount:
            config?.questionCount || result.questions?.length || 0,
          startLevel,
          startXp,
          customConfig: gameMode === "custom" ? config : null,
        });
        return { success: true, ...result };
      } catch (err) {
        const message =
          err?.data?.error || err?.message || "Failed to start game";
        dispatch({ type: "START_FAILED" });
        setError(message);
        return { success: false, error: message };
      } finally {
        startLock.current = false;
      }
    },
    [bootstrapSession, startDaily, startNewGame, userStats]
  );

  const startQuickPlay = useCallback(
    () => beginSession(withSport(QUICK_PLAY_CONFIG, sport), "quick"),
    [beginSession, sport]
  );

  const startDailyPlay = useCallback(
    () => beginSession(withSport(DAILY_PLAY_CONFIG, sport), "daily"),
    [beginSession, sport]
  );

  const startCustomPlay = useCallback(
    (config) =>
      beginSession(
        withSport({ ...config, gameMode: "custom" }, sport),
        "custom"
      ),
    [beginSession, sport]
  );

  const openCustom = useCallback(() => {
    dispatch({ type: "OPEN_CUSTOM" });
  }, []);

  const advanceAfterFeedback = useCallback(
    async (answerResult) => {
      const result = answerResult ?? pendingFeedbackResult.current ?? state.lastResult;
      pendingFeedbackResult.current = null;

    if (result?.nextQuestion != null) {
      dispatch({ type: "FEEDBACK_DONE" });
      const nextQ = sessionData?.questions?.[result.nextQuestion];
      const nextLimit = nextQ?.timeLimit || timeLimit;
      setTimeLimit(nextLimit);
      setTimeLeft(nextLimit);
      questionStartTime.current = Date.now();
      return;
    }

      dispatch({ type: "FEEDBACK_DONE" });
      const finishResult = await finishGame();
      if (finishResult.success) {
        dispatch({
          type: "COMPLETE_SUCCESS",
          results: {
            ...finishResult,
            gameMode: state.gameMode,
            bestStreakThisGame: state.bestStreakThisGame,
          },
        });
      }
    },
    [
      finishGame,
      sessionData,
      state.bestStreakThisGame,
      state.gameMode,
      state.lastResult,
      timeLimit,
    ]
  );

  const selectAnswer = useCallback(
    async (answer) => {
      if (state.phase !== "answering" || isSubmitting) return;
      dispatch({ type: "ANSWER_LOCK", answer });

      const question = sessionData?.questions?.[state.currentQuestionIndex];
      const questionId = question?.questionId || question?._id;
      const timeSpent = Date.now() - questionStartTime.current;
      const result = await answerQuestion(questionId, answer, timeSpent);

      if (result.success) {
        pendingFeedbackResult.current = result;
        dispatch({ type: "ANSWER_RESULT", result });
      } else {
        setError(result.error);
      }
    },
    [
      answerQuestion,
      isSubmitting,
      sessionData,
      state.currentQuestionIndex,
      state.phase,
    ]
  );

  const continueAfterFeedback = useCallback(() => {
    advanceAfterFeedback(pendingFeedbackResult.current);
  }, [advanceAfterFeedback]);

  useEffect(() => {
    if (state.view !== "playing" || state.phase !== "answering") return undefined;
    if (timeLeft <= 0) {
      selectAnswer(null);
      return undefined;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [selectAnswer, state.phase, state.view, timeLeft]);

  const returnToHub = useCallback(() => {
    pendingFeedbackResult.current = null;
    resetGame();
    dispatch({ type: "RETURN_HUB" });
    setError(null);
  }, [resetGame]);

  const confirmQuit = useCallback(async () => {
    await quitGame();
    setShowQuitDialog(false);
    returnToHub();
  }, [quitGame, returnToHub]);

  const dismissToast = useCallback(() => {
    dispatch({ type: "DISMISS_TOAST" });
  }, []);

  const setCommunityTab = useCallback((tab) => {
    dispatch({ type: "SET_COMMUNITY_TAB", tab });
  }, []);

  const currentQuestion = sessionData?.questions?.[state.currentQuestionIndex];

  return {
    view: state.view,
    phase: state.phase,
    gameMode: state.gameMode,
    currentQuestionIndex: state.currentQuestionIndex,
    currentQuestion,
    selectedAnswer: state.selectedAnswer,
    lastResult: state.lastResult,
    timeLeft,
    timeLimit,
    displayScore: state.displayScore || sessionData?.score || 0,
    displayStreak: state.displayStreak,
    toastQueue: state.toastQueue,
    results: state.results,
    startLevel: state.startLevel,
    startXp: state.startXp,
    bestStreakThisGame: state.bestStreakThisGame,
    lastCustomConfig: state.lastCustomConfig,
    communityTab: state.communityTab,
    setCommunityTab,
    reducedMotion,
    openCustom,
    startQuickPlay,
    startDailyPlay,
    startCustomPlay,
    selectAnswer,
    continueAfterFeedback,
    returnToHub,
    showQuitDialog,
    setShowQuitDialog,
    confirmQuit,
    dismissToast,
    sessionData,
    isStarting: state.view === "starting" || isStartRequestPending,
    isSubmitting,
    error,
    questionCount: state.questionCount,
  };
};
